import vtkGenericRenderWindow from 'vtk.js/Sources/Rendering/Misc/GenericRenderWindow';
import vtkGLTFImporter from 'vtk.js/Sources/IO/Geometry/GLTFImporter';
import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkPolyData from 'vtk.js/Sources/Common/DataModel/PolyData';
import vtkPoints from 'vtk.js/Sources/Common/Core/Points';
import vtkSphereMapper from 'vtk.js/Sources/Rendering/Core/SphereMapper';
import vtkPointPicker from 'vtk.js/Sources/Rendering/Core/PointPicker';
import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';
import vtkSphereSource from 'vtk.js/Sources/Filters/Sources/SphereSource';
import { verifiedScene, applyDisplayMatrix, cellColor, finitePosition, hasSceneMapping, displayBounds } from './merfish-scene';
// vtk.js keeps WebGL backends in opt-in profiles; without this side-effect
// import vtkRenderer has no OpenGL view-node factory and the canvas stays blank.
import 'vtk.js/Sources/Rendering/OpenGL/Profiles/Geometry';
import 'vtk.js/Sources/Rendering/OpenGL/Profiles/Molecule';
import { isBrowserRenderableAsset, limitVolumeTiles, safeOpacity } from './render-guards';

/** The UI names are deliberately independent from the server's numeric LOD. */
export const ATLAS_LOD_NAMES = Object.freeze({ coarse: 0, medium: 1, detailed: 2 });

export function selectAtlasLOD(meshes = [], requested = 'medium') {
  const available = meshes.filter((mesh) => Number.isInteger(Number(mesh?.lod)))
    .sort((left, right) => Number(left.lod) - Number(right.lod));
  if (!available.length) return null;
  const wanted = typeof requested === 'string'
    ? (ATLAS_LOD_NAMES[requested] ?? Number(requested))
    : Number(requested);
  const target = Number.isFinite(wanted) ? wanted : ATLAS_LOD_NAMES.medium;
  return available.reduce((best, mesh) =>
    Math.abs(Number(mesh.lod) - target) < Math.abs(Number(best.lod) - target) ? mesh : best,
  available[0]);
}

/** Apply a contract affine transform to an atlas/entity position. */
export function transformAtlasPosition(position, transform) {
  if (!Array.isArray(position) || position.length < 3) return null;
  const matrix = transform?.matrix;
  if (!Array.isArray(matrix) || ![3, 4].includes(matrix.length)) return null;
  const point = position.slice(0, 3).map(Number);
  if (point.some((value) => !Number.isFinite(value))) return null;
  if (matrix.length === 3) return matrix.map((row) => row.reduce((sum, value, index) => sum + Number(value) * point[index], 0));
  const values = matrix.map((row) => row.slice(0, 4).map(Number));
  const result = values.slice(0, 3).map((row) => row[0] * point[0] + row[1] * point[1] + row[2] * point[2] + row[3]);
  const w = values[3][0] * point[0] + values[3][1] * point[1] + values[3][2] * point[2] + values[3][3];
  return Number.isFinite(w) && w !== 0 ? result.map((value) => value / w) : result;
}

/**
 * Some derived GLBs retain the source volume's axis order and centered origin
 * even when their API registration is identity. Derive the affine that maps
 * the imported bounds into the declared coordinate-space bounds so overlays
 * use the same anatomical frame as the point coordinates.
 */
export function deriveBoundsRegistrationMatrix(sourceBounds, targetBounds) {
  if (!Array.isArray(sourceBounds) || sourceBounds.length < 6 || !Array.isArray(targetBounds) || targetBounds.length < 6) return null;
  const sourceMin = [sourceBounds[0], sourceBounds[2], sourceBounds[4]].map(Number);
  const sourceMax = [sourceBounds[1], sourceBounds[3], sourceBounds[5]].map(Number);
  const targetMin = [targetBounds[0], targetBounds[2], targetBounds[4]].map(Number);
  const targetMax = [targetBounds[1], targetBounds[3], targetBounds[5]].map(Number);
  const sourceSpan = sourceMax.map((value, index) => value - sourceMin[index]);
  const targetSpan = targetMax.map((value, index) => value - targetMin[index]);
  if ([...sourceSpan, ...targetSpan].some((value) => !Number.isFinite(value) || value <= 0)) return null;
  const permutations = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]];
  const permutation = permutations.reduce((best, candidate) => {
    const score = candidate.reduce((sum, sourceAxis, targetAxis) => sum + Math.abs(Math.log(targetSpan[targetAxis] / sourceSpan[sourceAxis])), 0);
    return !best || score < best.score ? { candidate, score } : best;
  }, null)?.candidate;
  if (!permutation) return null;
  const matrix = [0, 1, 2].map((targetAxis) => {
    const sourceAxis = permutation[targetAxis];
    const scale = targetSpan[targetAxis] / sourceSpan[sourceAxis];
    const row = [0, 0, 0, targetMin[targetAxis] - sourceMin[sourceAxis] * scale];
    row[sourceAxis] = scale;
    return row;
  });
  return [...matrix, [0, 0, 0, 1]];
}

/**
 * Allen CCF GLBs generated by utility/generate_allen_meshes.py retain the
 * NIfTI affine's source axes: [LR, -AP, -DV] with a centered origin. The
 * spatial entities are already [AP, DV, LR] in micrometres. This explicit
 * source-to-CCF affine is therefore required before both can share a scene.
 */
export function allenCcfV3MeshMatrix(mesh) {
  if (mesh?.coordinate_space_id !== 'ccf-v3' || !String(mesh?.asset?.asset_id || '').startsWith('asset-mesh-')) return null;
  return [
    [0, -1, 0, 5350],
    [0, 0, -1, 5220],
    [1, 0, 0, 5695],
    [0, 0, 0, 1],
  ];
}

/** Convert an already-registered CCF point into the native Allen GLB frame. */
export function pointToAllenCcfMeshPosition(position, mesh) {
  if (!Array.isArray(position) || position.length < 3) return null;
  const point = position.slice(0, 3).map(Number);
  if (point.some((value) => !Number.isFinite(value))) return null;
  if (mesh?.coordinate_space_id === 'ccf-v3' && String(mesh?.asset?.asset_id || '').startsWith('asset-mesh-')) {
    return [point[2] - 5695, 5350 - point[0], 5220 - point[1]];
  }
  return point;
}

export function atlasAssetState(mesh) {
  if (!mesh) return { state: 'unavailable', message: 'Atlas mesh metadata is unavailable.' };
  if (mesh.state !== 'ready') return { state: mesh.state || 'unavailable', message: mesh.status_reason || `Atlas mesh is ${mesh.status || mesh.state || 'unavailable'}.` };
  if (!mesh.registration?.coordinate_space_id || !mesh.registration?.transform) return { state: 'unregistered', message: 'Atlas mesh has no coordinate registration and cannot be rendered.' };
  if (!isBrowserRenderableAsset(mesh.asset, { kind: 'mesh' })) return { state: 'unavailable', message: 'Atlas geometry is not a browser-renderable derived asset.' };
  return { state: 'ready', message: '' };
}

function vtkUserMatrix(transform) {
  const matrix = transform?.matrix;
  if (!Array.isArray(matrix)) return null;
  // API transforms are row-major JSON; vtk.js matrix arrays are column-major.
  if (matrix.length === 4) return new Float64Array([0, 1, 2, 3].flatMap((column) => [0, 1, 2, 3].map((row) => Number(matrix[row][column]))));
  if (matrix.length === 3) return new Float64Array([
    Number(matrix[0][0]), Number(matrix[1][0]), Number(matrix[2][0]), 0,
    Number(matrix[0][1]), Number(matrix[1][1]), Number(matrix[2][1]), 0,
    Number(matrix[0][2]), Number(matrix[1][2]), Number(matrix[2][2]), 0,
    0, 0, 0, 1,
  ]);
  return null;
}

function deleteMeshImporter(value) {
  try { value?.delete(); }
  catch (error) {
    // vtk.js 36.6 clears its actor maps, then calls clear() on a WeakMap.
    // WeakMap entries are GC-owned. Do not let that upstream cleanup defect
    // prevent our renderer/context disposal (including access revocation).
    // Production minification renames imageBufferViewCache; retain the specific
    // missing WeakMap.clear signature instead of depending on its variable name.
    if (!(error instanceof TypeError) || !/\bclear is not a function$/.test(error.message)) throw error;
  }
}

/**
 * Render one registered GLB atlas mesh with vtk.js. Volume and labelmap assets
 * use the same controller contract through createContextLayer below, allowing
 * their readers to be added without changing scene state or controls.
 */
export function createAtlasMeshLayer(options = {}) {
  const { container, onPointSelect, onStatus } = options;
  let props = { points: [], pointsReady: true, visible: true, pointVisible: true, opacity: .35, pointOpacity: 1, color: [.35,.55,.9], pickEnabled: true, ...options };
  props.opacity = safeOpacity(props.opacity, .35); props.pointOpacity = safeOpacity(props.pointOpacity);
  let disposed = false, state = 'loading', message = '', meshSequence = 0, fitted = false;
  // Once the viewer has framed the scene it must not silently re-frame a
  // camera the user is driving. Until they take control, a container resize
  // should re-fit: on a phone the mesh and the final layout both arrive
  // after the first fit, which otherwise leaves the scene stuck at a zoom
  // computed for the wrong viewport.
  let userCamera = false;
  let renderer, renderWindow, observer, canvas, loadAbort, importer, contextRecovery;
  let atlasActors = [], alignedPoints = [], subscriptions = [], contextLost = false;
  let pendingCamera = options.camera;
  const nativePreview = () => props.merfish && !hasSceneMapping(props.registration);
  const pointRadius = () => {
    if (!nativePreview()) return props.merfish ? 12 : 38;
    if (['millimeter', 'millimeters'].includes(props.coordinateUnits)) return .012;
    if (['micrometer', 'micrometers'].includes(props.coordinateUnits)) return 12;
    throw new Error('Native 3D preview requires declared millimeter or micrometer units.');
  };
  const publishCamera = () => {
    if (!props.onCameraChange || !renderer || !fitted || disposed) return;
    const camera=renderer.getActiveCamera();
    props.onCameraChange({position:[...camera.getPosition()],target:[...camera.getFocalPoint()],viewUp:[...camera.getViewUp()],viewAngle:camera.getViewAngle(),parallelScale:camera.getParallelScale(),zoom:30/camera.getViewAngle(),rotation:0,pan:[0,0],frameId:props.registration?.transforms?.points?.target?.frame_id || props.coordinateSpaceId});
  };
  const restoreCamera = () => {
    const value=pendingCamera;
    if (!fitted || !value?.viewUp || !renderer) return;
    pendingCamera=null;
    // An old native-mm camera is not a valid pose for the new RAS-um scene.
    const frame = props.registration?.transforms?.points?.target?.frame_id;
    if ((value.frameId && value.frameId !== (frame || props.coordinateSpaceId)) || (props.registration?.state === 'engineering_preview' && value.frameId !== frame)) return;
    if (![value.position,value.target,value.viewUp].every(finitePosition) || !(value.viewAngle>0&&value.viewAngle<180) || !(value.parallelScale>0)) return;
    const camera=renderer.getActiveCamera();
    camera.setPosition(...value.position);camera.setFocalPoint(...value.target);camera.setViewUp(...value.viewUp);
    camera.setViewAngle(value.viewAngle);camera.setParallelScale(value.parallelScale);render();
  };
  const owned = [];
  const hasViewport = () => { const bounds=container?.getBoundingClientRect?.(); return !bounds || (bounds.width > 0 && bounds.height > 0); };
  const own = (object) => { owned.push(object); return object; };
  const status = (value, detail = '') => { state = value; message = detail; onStatus?.({state, message}); };
  let renderFrame = null;
  const draw = () => {
    renderFrame = null;
    if (disposed || contextLost || !renderWindow || !hasViewport()) return;
    try { renderer.resetCameraClippingRange(); renderWindow.getRenderWindow().render(); }
    catch { status('error', '3D rendering failed. Retry the scene; Data remains available.'); }
  };
  // React may update colors, properties and camera in one commit. Submit one
  // GPU frame after those updates instead of synchronously drawing each change.
  const render = () => {
    if (disposed || contextLost || renderFrame !== null) return;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') renderFrame = window.requestAnimationFrame(draw);
    else draw();
  };
  const fit = () => {
    if (!renderer || !renderWindow || contextLost || !hasViewport()) return false;
    // Camera bounds include hidden layers too; scientific arrays are untouched.
    const actors = [...atlasActors, pointActor];
    const visibilities = actors.map((actor) => actor.getVisibility());
    actors.forEach((actor) => actor.setVisibility(true));
    renderWindow.resize();
    if (props.fullBounds) {
      const matrix = props.merfish && !nativePreview() ? verifiedScene(props.registration, props.mesh, props.coordinateSpaceId).points : null;
      renderer.resetCamera(displayBounds(props.fullBounds, matrix));
    } else renderer.resetCamera();
    actors.forEach((actor, index) => actor.setVisibility(visibilities[index]));
    render();
  };
  const releaseMesh = () => {
    atlasActors.forEach((actor) => { renderer?.removeActor(actor); actor.getMapper()?.delete(); actor.delete(); });
    atlasActors = [];
    const previous = importer; importer = null; deleteMeshImporter(previous);
  };
  let pointActor, pointData, pointBuffer, pointMapper, colorBuffer;
  let selectedActor, selectedData, selectedBuffer, outlineActor, selectedMapper, outline;
  const currentSelection = () => alignedPoints.find((point) => point.entityId === props.selectedEntityId);
  const focusPosition = () => {
    const selected = currentSelection();
    if (selected) return selected.position;
    if (!finitePosition(props.focusPosition)) return null;
    try {
      const matrix = props.merfish && !nativePreview() ? verifiedScene(props.registration, props.mesh, props.coordinateSpaceId).points : null;
      return matrix ? applyDisplayMatrix(props.focusPosition, matrix) : [...props.focusPosition];
    } catch (_) { return null; }
  };
  const selection = () => {
    const selected = currentSelection();
    selectedBuffer?.setData(new Float32Array(selected?.position || []), 3);
    selectedData?.getVerts().setData(selected ? new Uint32Array([1,0]) : new Uint32Array());
    selectedData?.modified();
    selectedActor?.setVisibility(Boolean(selected) && props.pointVisible);
    outlineActor?.setVisibility(Boolean(selected) && props.pointVisible);
    selectedActor?.getProperty().setOpacity(props.pointOpacity);
    outlineActor?.getProperty().setOpacity(props.pointOpacity);
    if (selected) outlineActor.setPosition(...selected.position);
  };
  let positions = new Float32Array(0), vertices = new Uint32Array(0), colors = new Uint8Array(0);
  const recolor = () => {
    alignedPoints.forEach((point,index) => colors.set(props.merfish ? cellColor(point.source, props.encoding) : [255,184,31], index*3));
    colorBuffer.setData(colors.subarray(0, alignedPoints.length*3),3); pointData.modified();
  };
  const rebuildPoints = () => {
    if (!pointData) return;
    // Point data (from the spatial query) and the atlas mesh list are two
    // independent fetches; points routinely become ready before the mesh
    // does. update() calls this directly off its own {points,pointsReady}
    // effect, outside loadMesh()'s "mesh not loaded yet" guard, so without
    // this check a validated-alignment scene reads props.mesh as null here
    // and verifiedScene throws -- misreported as a real mesh/frame mismatch
    // and latched as "unavailable" even though the mesh is simply still in
    // flight. Defer silently; loadMesh() re-runs this once the mesh arrives.
    if (props.merfish && !nativePreview() && !props.mesh) { status('loading'); return; }
    const matrix = props.merfish && !nativePreview() ? verifiedScene(props.registration, props.mesh, props.coordinateSpaceId).points : null;
    pointMapper.setRadius(pointRadius());
    selectedMapper?.setRadius(pointRadius()*1.8);
    outline?.setRadius?.(pointRadius()*1.8*1.25);
    if (props.merfish && props.points.length > (props.pointLimit || 10000)) throw new Error('MERFISH view exceeds its point limit.');
    alignedPoints = props.points.map((point) => {
      if (!finitePosition(point.position)) throw new Error('Nonfinite source point.');
      if (props.merfish && point.coordinate_space_id !== props.coordinateSpaceId) throw new Error('Cell source frame does not match this release.');
      const position = props.merfish ? (matrix ? applyDisplayMatrix(point.position, matrix) : [...point.position]) : pointToAllenCcfMeshPosition(point.position, props.mesh);
      return {entityId:point.entity_id, position, source:point};
    });
    if (positions.length < alignedPoints.length*3) {
      positions = new Float32Array(alignedPoints.length*3);
      vertices = new Uint32Array(alignedPoints.length*2);
      colors = new Uint8Array(alignedPoints.length*3);
    }
    alignedPoints.forEach((point,index) => {
      positions.set(point.position,index*3); vertices.set([1,index],index*2);
    });
    pointBuffer.setData(positions.subarray(0,alignedPoints.length*3),3);
    pointData.getVerts().setData(vertices.subarray(0,alignedPoints.length*2));
    recolor();
    pointActor.setVisibility(props.pointVisible); pointActor.getProperty().setOpacity(props.pointOpacity); selection();
  };
  const cameraAction = (action) => {
    if (!renderer || contextLost) return;
    // "Reset" hands framing back to the viewer; anything else is the user
    // choosing their own camera, which a later resize must not overwrite.
    if (action === 'reset') { userCamera = false; fit(); publishCamera(); return; }
    userCamera = true;
    const camera = renderer.getActiveCamera();
    const position = camera.getPosition(), focal = camera.getFocalPoint();
    if (action === 'focus') {
      const selectedPosition = focusPosition(); if (!selectedPosition) return;
      const delta = selectedPosition.map((v,i)=>v-focal[i]);
      camera.setPosition(...position.map((v,i)=>v+delta[i])); camera.setFocalPoint(...selectedPosition);
    }
    if (action === 'rotateLeft') camera.azimuth(-15);
    if (action === 'rotateRight') camera.azimuth(15);
    if (action === 'zoomIn') camera.zoom(1.25);
    if (action === 'zoomOut') camera.zoom(.8);
    if (['left','right','up','down'].includes(action)) {
      const normalize = (v) => { const n=Math.hypot(...v)||1; return v.map(x=>x/n); };
      const cross = (a,b) => [a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
      const forward = focal.map((v,i)=>v-position[i]), right = normalize(cross(forward,camera.getViewUp())), up = normalize(cross(right,forward));
      const axis = ['left','right'].includes(action) ? right : up;
      const amount = Math.hypot(...forward)*.1*(['left','down'].includes(action)?-1:1);
      const shift = axis.map(v=>v*amount);
      camera.setPosition(...position.map((v,i)=>v+shift[i])); camera.setFocalPoint(...focal.map((v,i)=>v+shift[i]));
    }
    camera.orthogonalizeViewUp(); render(); publishCamera();
  };
  const loadMesh = async () => {
    const sequence = ++meshSequence;
    loadAbort?.abort(); loadAbort = new AbortController();
    const mesh = props.mesh;
    let parsingImporter = null;
    try {
      releaseMesh();
      pointActor?.setVisibility(false); selectedActor?.setVisibility(false); outlineActor?.setVisibility(false);
      if (nativePreview()) {
        rebuildPoints();
        status('ready', 'Native-coordinate points only; anatomical overlay not validated.');
        if (!fitted && props.pointsReady) fitted = fit() !== false;
        else render();
        restoreCamera();
        return;
      }
      // The collection mounts its viewer before the mesh manifest arrives.
      // Keep the canvas in its initial loading state rather than briefly
      // reporting an unavailable 3D view for that expected transition.
      if (!mesh) { status('loading'); return; }
      const assetState = atlasAssetState(mesh);
      if (assetState.state !== 'ready') throw new Error(assetState.message);
      const matrices = props.merfish ? verifiedScene(props.registration, mesh, props.coordinateSpaceId) : null;
      rebuildPoints();
      status('loading', 'Loading validated anatomy…');
      const response = await fetch(mesh.asset.browser_url || mesh.asset.storage_uri, {signal:loadAbort.signal, cache:'no-store'});
      if (!response.ok) throw new Error('Atlas geometry request failed ('+response.status+').');
      const bytes = await response.arrayBuffer();
      if (disposed || sequence !== meshSequence) return;
      if (props.merfish) {
        const expected = String(mesh.asset.checksum || '').replace(/^sha256:/, '');
        if (!/^[0-9a-f]{64}$/.test(expected) || !globalThis.crypto?.subtle) throw new Error('Mesh SHA256 verification unavailable.');
        const actual = Array.from(new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256',bytes)),(value)=>value.toString(16).padStart(2,'0')).join('');
        if (actual !== expected) throw new Error('Mesh bytes do not match the approved checksum.');
        if (disposed || sequence !== meshSequence) return;
      }
      const localImporter = vtkGLTFImporter.newInstance({renderer});
      parsingImporter = localImporter;
      // parseAsBinary resolves after vtk objects are built (setUrl does not).
      // Do not delete an importer mid-parse; stale completions dispose locally.
      await localImporter.parseAsBinary(bytes);
      if (disposed || sequence !== meshSequence) {
        for (const item of localImporter.getActors().values()) { item.getMapper()?.delete(); item.delete(); }
        deleteMeshImporter(localImporter); return;
      }
      importer = localImporter;
            localImporter.importActors(); atlasActors = [...localImporter.getActors().values()];
            if (!atlasActors.length) throw new Error('Mesh contains no renderable actors.');
            const userMatrix = vtkUserMatrix(matrices ? {matrix:matrices.mesh} : mesh.registration.transform);
            atlasActors.forEach((actor) => {
              if (userMatrix) {
                const native = actor.getUserMatrix();
                const composed = new Float64Array(16);
                for (let col=0;col<4;col++) for(let row=0;row<4;row++) {
                  composed[col*4+row] = [0,1,2,3].reduce((sum,k)=>sum+userMatrix[k*4+row]*native[col*4+k],0);
                }
                actor.setUserMatrix(composed);
              }
              actor.getProperty().setOpacity(props.opacity); actor.getProperty().setColor(...props.color);
              actor.setVisibility(props.visible);
            });
            status('ready');
            if (!fitted && props.pointsReady) { fitted = fit() !== false; } else render();
            restoreCamera();
    } catch(error) {
      if (parsingImporter && parsingImporter !== importer) {
        for (const item of parsingImporter.getActors()?.values() || []) { item.getMapper()?.delete(); item.delete(); }
        deleteMeshImporter(parsingImporter);
      }
      if (disposed || sequence !== meshSequence || error.name === 'AbortError') return;
      status('error', error.message || 'Atlas unavailable. Data remains usable.');
      render();
    }
  };
  const controller = {
    get state() { return state; }, get message() { return message; },
    ready: Promise.resolve(),
    update(next = {}) {
      if (disposed) return;
      if (next.retry && contextLost) {
        // getExtension may return null while the context is lost. Retain the
        // extension acquired while healthy so the explicit retry can restore it.
        contextRecovery?.restoreContext();
        return;
      }
      const meshChanged = next.mesh !== undefined && next.mesh !== props.mesh;
      const registrationChanged = next.registration !== undefined && next.registration !== props.registration;
      const frameChanged = (next.coordinateSpaceId !== undefined && next.coordinateSpaceId !== props.coordinateSpaceId) || (next.coordinateUnits !== undefined && next.coordinateUnits !== props.coordinateUnits) || (next.merfish !== undefined && next.merfish !== props.merfish);
      const pointsChanged = next.points !== undefined && next.points !== props.points;
    const encodingChanged = next.encoding !== undefined && next.encoding !== props.encoding;
    props = {...props, ...next};
      if (next.camera !== undefined) pendingCamera=next.camera;
      props.opacity = safeOpacity(props.opacity, .35); props.pointOpacity = safeOpacity(props.pointOpacity);
      if (meshChanged || registrationChanged || frameChanged || next.retry) { controller.ready = loadMesh(); return; }
      try {
        if (pointsChanged) rebuildPoints();
        else if (encodingChanged) recolor();
        if (next.opacity !== undefined) atlasActors.forEach(actor=>actor.getProperty().setOpacity(props.opacity));
        if (next.visible !== undefined) atlasActors.forEach(actor=>actor.setVisibility(props.visible));
        pointActor?.setVisibility(props.pointVisible);
        pointActor?.getProperty().setOpacity(props.pointOpacity);
        selectedActor?.getProperty().setOpacity(props.pointOpacity);
        outlineActor?.getProperty().setOpacity(props.pointOpacity);
        selection();
        if (!fitted && props.pointsReady && (atlasActors.length || alignedPoints.length)) fitted = fit() !== false;
        restoreCamera();
        if (next.cameraAction) cameraAction(next.cameraAction); else render();
      } catch(error) { alignedPoints=[]; pointActor?.setVisibility(false); selection(); status('unavailable',error.message); }
    },
    dispose() {
      if (disposed) return;
      disposed = true; meshSequence++; loadAbort?.abort(); observer?.disconnect();
      if (renderFrame !== null) window.cancelAnimationFrame?.(renderFrame); renderFrame = null;
      subscriptions.forEach(item=>item?.unsubscribe());
      canvas?.removeEventListener('webglcontextlost', lost);
      canvas?.removeEventListener('webglcontextrestored', restored);
      releaseMesh();
      [pointActor,selectedActor,outlineActor].forEach(actor=>renderer?.removeActor(actor));
      owned.reverse().forEach(object=>object.delete?.());
      alignedPoints=[]; positions=new Float32Array(0); vertices=new Uint32Array(0); colors=new Uint8Array(0);
      const interactor = renderWindow?.getInteractor(); interactor?.unbindEvents();
      const coreWindow = renderWindow?.getRenderWindow();
      const sceneRenderer = renderer;
      const gl = renderWindow?.getApiSpecificRenderWindow()?.getContext?.();
      renderWindow?.delete(); gl?.getExtension('WEBGL_lose_context')?.loseContext();
      interactor?.delete?.(); sceneRenderer?.delete?.(); coreWindow?.delete?.();
      renderWindow=null; renderer=null;
    },
  };
  const lost = (event) => { event.preventDefault(); contextLost=true; status('error','WebGL context lost. Retry 3D; Data and evidence remain available.'); };
  const restored = () => { contextLost=false; controller.ready=loadMesh(); };
  if (!container || typeof window === 'undefined') return controller;
  try {
    renderWindow = vtkGenericRenderWindow.newInstance({background:[.035,.05,.08],listenWindowResize:false});
    renderWindow.setContainer(container); renderer=renderWindow.getRenderer();
    pointData=own(vtkPolyData.newInstance()); pointBuffer=own(vtkPoints.newInstance());
    pointData.setPoints(pointBuffer);
    colorBuffer=own(vtkDataArray.newInstance({name:'CellColors',numberOfComponents:3,values:new Uint8Array()}));
    pointData.getPointData().setScalars(colorBuffer);
    pointMapper=own(vtkSphereMapper.newInstance({radius:props.merfish ? 12 : 38}));
    pointMapper.setInputData(pointData); pointMapper.setColorModeToDirectScalars(); pointMapper.setScalarVisibility(true);
    pointActor=own(vtkActor.newInstance()); pointActor.setMapper(pointMapper); renderer.addActor(pointActor);
    selectedData=own(vtkPolyData.newInstance()); selectedBuffer=own(vtkPoints.newInstance()); selectedData.setPoints(selectedBuffer);
    const radius=pointRadius()*1.8;
    selectedMapper=own(vtkSphereMapper.newInstance({radius})); selectedMapper.setInputData(selectedData); selectedMapper.setScalarVisibility(false);
    selectedActor=own(vtkActor.newInstance()); selectedActor.setMapper(selectedMapper); selectedActor.getProperty().setColor(.95,.05,.05);
    renderer.addActor(selectedActor);
    outline=own(vtkSphereSource.newInstance({radius:radius*1.25,thetaResolution:12,phiResolution:8}));
    const outlineMapper=own(vtkMapper.newInstance()); outlineMapper.setInputConnection(outline.getOutputPort());
    outlineActor=own(vtkActor.newInstance()); outlineActor.setMapper(outlineMapper); outlineActor.getProperty().setRepresentationToWireframe(); outlineActor.getProperty().setColor(1,1,1);
    renderer.addActor(outlineActor); selection();
    const picker=own(vtkPointPicker.newInstance()); picker.setPickFromList(true); picker.setPickList([pointActor]); picker.setTolerance(.01);
    const cameraSettled=renderWindow.getInteractor().onEndAnimation?.(()=>{userCamera=true;publishCamera();});
    if(cameraSettled) subscriptions.push(cameraSettled);
    let press=null;
    subscriptions.push(renderWindow.getInteractor().onLeftButtonPress(event=>{press={...event.position};}));
    subscriptions.push(renderWindow.getInteractor().onLeftButtonRelease(event=>{
      const start=press; press=null;
      if (!start || Math.hypot(event.position.x-start.x,event.position.y-start.y)>4 || !props.pointVisible || props.pointOpacity <= 0 || !props.pickEnabled) return;
      picker.pick([event.position.x,event.position.y,0],renderer);
      const id=alignedPoints[picker.getPointId()]?.entityId;
      if(id) props.onPointSelect?.(id);
    }));
    canvas=container.querySelector('canvas');
    canvas?.addEventListener('webglcontextlost',lost); canvas?.addEventListener('webglcontextrestored',restored);
    observer=typeof ResizeObserver==='function'?new ResizeObserver(()=>{if(!disposed&&!contextLost&&hasViewport()){const framable=props.pointsReady&&(atlasActors.length||alignedPoints.length);if((!fitted||!userCamera)&&framable) fitted=fit()!==false; else {renderWindow.resize();render();}}}):null;
    observer?.observe(container);
    renderWindow.resize();
    contextRecovery=renderWindow.getApiSpecificRenderWindow()?.getContext?.()?.getExtension('WEBGL_lose_context');
    controller.ready=loadMesh();
  } catch(error) { status('error',error.message); }
  return controller;
}

export function createContextLayer({ kind = 'volume', asset, ...options } = {}) {
  const state = asset?.state || 'unavailable';
  const tiles = limitVolumeTiles(asset?.tiles || asset?.tile_manifest?.tiles || [], options);
  const renderable = isBrowserRenderableAsset(asset, { kind });
  const message = renderable
    ? `${kind} tile access is prepared (${tiles.length || 'manifest'} bounded tiles).`
    : `${kind === 'labelmap' ? 'Labelmap' : 'Volume'} asset is ${state}.`;
  return { kind, state: renderable ? 'ready' : state, message, tiles, options, dispose() {} };
}

export const createVolumeLayer = (options = {}) => createContextLayer({ ...options, kind: 'volume' });
export const createLabelmapLayer = (options = {}) => createContextLayer({ ...options, kind: 'labelmap' });
